import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { mockVendors } from '../data/mockVendors'
import LoginForm from './LoginForm'
import RegisterForm from './RegisterForm'
import VendorApplicationForm from './VendorApplicationForm'
import NotificationSystem from './NotificationSystem'

export default function Navigation() {
  const { user, isLoggedIn, logout } = useAuth()
  const navigate = useNavigate()
  const [showLoginForm, setShowLoginForm] = useState(false)
  const [showRegisterForm, setShowRegisterForm] = useState(false)
  const [showVendorApplication, setShowVendorApplication] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [desktopSearchQuery, setDesktopSearchQuery] = useState('')
  const [mobileSearchQuery, setMobileSearchQuery] = useState('')
  const [showDesktopSuggestions, setShowDesktopSuggestions] = useState(false)
  const [showMobileSuggestions, setShowMobileSuggestions] = useState(false)

  // Generate search suggestions from vendors and products
  const getSearchSuggestions = (query: string) => {
    if (!query || query.length < 1) return []
    
    const suggestions: Array<{text: string, type: 'vendor' | 'product' | 'category', data?: any}> = []
    const queryLower = query.toLowerCase()
    
    // Add vendor suggestions
    mockVendors.forEach(vendor => {
      if (vendor.name.toLowerCase().includes(queryLower)) {
        suggestions.push({
          text: vendor.name,
          type: 'vendor',
          data: vendor
        })
      }
    })
    
    // Add product suggestions from all vendors
    mockVendors.forEach(vendor => {
      vendor.products?.forEach(product => {
        if (product.name.toLowerCase().includes(queryLower) || 
            product.extractType?.toLowerCase().includes(queryLower) ||
            product.description?.toLowerCase().includes(queryLower)) {
          suggestions.push({
            text: `${product.name} (${vendor.name})`,
            type: 'product',
            data: { product, vendor }
          })
        }
      })
    })
    
    // Add category/term suggestions
    const categories = [
      // Extract Types
      '7OH powder', '7OH tablets', 'MIT extract', 'MIT powder', 'MIT tablets', 
      'Full spectrum extract', 'Liquid extract', 'Kratom concentrate',
      'Thai 7OH', 'Domestic 7OH', 'MIT salt', 'MIT freebase',
      
      // Popular Strains
      'Red Bali', 'Green Maeng Da', 'White Thai', 'Red Maeng Da', 'Green Bali',
      'White Maeng Da', 'Red Thai', 'Green Thai', 'White Bali', 'Red Borneo',
      'Green Borneo', 'White Borneo', 'Red Indo', 'Green Indo', 'White Indo',
      'Gold Bali', 'Yellow Vietnam', 'Red Dragon', 'Green Dragon', 'White Dragon',
      
      // Product Forms
      'Kratom capsules', 'Kratom powder', 'Kratom tablets', 'Kratom shots',
      'Kratom tea', 'Kratom gummies', 'Kratom tincture', 'Kratom softgels',
      
      // Effects
      'Energizing', 'Energetic', 'Stimulating', 'Euphoric', 'Sedating', 'Relaxing',
      'Pain relieving', 'Mood boosting', 'Focus enhancing', 'Calming', 'Uplifting',
      'Anxiety relief', 'Sleep aid', 'Stress relief', 'Motivation',
      
      // Potency & Quality
      'High potency', 'Premium quality', 'Lab tested', 'Organic kratom', 
      'Fresh kratom', 'Small batch', 'Ultra enhanced', 'Standardized extract',
      
      // Origins
      'Indonesian kratom', 'Malaysian kratom', 'Thai kratom', 'Vietnam kratom',
      'Cambodian kratom', 'Myanmar kratom', 'Domestic grown',
      
      // Colors/Vein Types  
      'Red vein', 'Green vein', 'White vein', 'Yellow vein', 'Gold kratom',
      
      // Popular Searches
      'Best kratom', 'Kratom for beginners', 'Strongest kratom', 'Cheap kratom',
      'Fast shipping', 'Bulk kratom', 'Kratom samples', 'COA verified'
    ]
    
    categories.forEach(category => {
      if (category.toLowerCase().includes(queryLower)) {
        suggestions.push({
          text: category,
          type: 'category'
        })
      }
    })
    
    // Remove duplicates and limit results
    const uniqueSuggestions = suggestions
      .filter((item, index, self) => 
        index === self.findIndex(t => t.text === item.text)
      )
      .slice(0, 8) // Show max 8 suggestions
    
    return uniqueSuggestions
  }

  // Find best match for direct navigation on Enter
  const findBestMatch = (query: string) => {
    if (!query || query.length < 2) return null
    
    const queryLower = query.toLowerCase().trim()
    
    // First, look for exact vendor name matches
    const exactVendorMatch = mockVendors.find(vendor => 
      vendor.name.toLowerCase() === queryLower
    )
    if (exactVendorMatch) {
      return { type: 'vendor', data: exactVendorMatch }
    }
    
    // Then look for partial vendor name matches
    const partialVendorMatch = mockVendors.find(vendor => 
      vendor.name.toLowerCase().includes(queryLower)
    )
    if (partialVendorMatch) {
      return { type: 'vendor', data: partialVendorMatch }
    }
    
    // Look for exact product matches
    for (const vendor of mockVendors) {
      const exactProductMatch = vendor.products?.find(product =>
        product.name.toLowerCase() === queryLower
      )
      if (exactProductMatch) {
        return { type: 'product', data: { product: exactProductMatch, vendor } }
      }
    }
    
    // Look for partial product matches
    for (const vendor of mockVendors) {
      const partialProductMatch = vendor.products?.find(product =>
        product.name.toLowerCase().includes(queryLower) ||
        product.extractType?.toLowerCase().includes(queryLower)
      )
      if (partialProductMatch) {
        return { type: 'product', data: { product: partialProductMatch, vendor } }
      }
    }
    
    return null // No direct match found, go to search page
  }

  const handleLoginClick = () => {
    setShowLoginForm(true)
  }

  const handleRegisterClick = () => {
    setShowRegisterForm(true)
  }

  const handleLogout = () => {
    logout()
  }

  const closeAuthForms = () => {
    setShowLoginForm(false)
    setShowRegisterForm(false)
    setShowVendorApplication(false)
  }

  const handleVendorApplicationClick = () => {
    setShowVendorApplication(true)
  }

  const switchToRegister = () => {
    setShowLoginForm(false)
    setShowRegisterForm(true)
  }

  const switchToLogin = () => {
    setShowRegisterForm(false)
    setShowLoginForm(true)
  }

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen)
  }

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false)
  }

  const handleDesktopSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setDesktopSearchQuery(value)
    setShowDesktopSuggestions(value.length > 0)
  }

  const handleMobileSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setMobileSearchQuery(value)
    setShowMobileSuggestions(value.length > 0)
  }

  const handleDesktopSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (desktopSearchQuery.trim()) {
      const bestMatch = findBestMatch(desktopSearchQuery)
      
      if (bestMatch?.type === 'vendor') {
        navigate(`/vendor/${bestMatch.data.id}`)
      } else if (bestMatch?.type === 'product') {
        navigate(`/vendor/${bestMatch.data.vendor.id}/product/${bestMatch.data.product.id}`)
      } else {
        navigate(`/search?q=${encodeURIComponent(desktopSearchQuery.trim())}`)
      }
      
      setShowDesktopSuggestions(false)
    }
  }

  const handleMobileSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (mobileSearchQuery.trim()) {
      const bestMatch = findBestMatch(mobileSearchQuery)
      
      if (bestMatch?.type === 'vendor') {
        navigate(`/vendor/${bestMatch.data.id}`)
      } else if (bestMatch?.type === 'product') {
        navigate(`/vendor/${bestMatch.data.vendor.id}/product/${bestMatch.data.product.id}`)
      } else {
        navigate(`/search?q=${encodeURIComponent(mobileSearchQuery.trim())}`)
      }
      
      setShowMobileSuggestions(false)
    }
  }

  const handleSuggestionClick = (suggestion: {text: string, type: string, data?: any}, isDesktop: boolean) => {
    if (isDesktop) {
      setDesktopSearchQuery(suggestion.text)
      setShowDesktopSuggestions(false)
    } else {
      setMobileSearchQuery(suggestion.text)
      setShowMobileSuggestions(false)
    }
    
    // Navigate based on suggestion type
    if (suggestion.type === 'vendor' && suggestion.data) {
      navigate(`/vendor/${suggestion.data.id}`)
    } else if (suggestion.type === 'product' && suggestion.data) {
      navigate(`/vendor/${suggestion.data.vendor.id}/product/${suggestion.data.product.id}`)
    } else {
      // For categories or general terms, go to search page
      navigate(`/search?q=${encodeURIComponent(suggestion.text)}`)
    }
  }


  const handleSearchFocus = (isDesktop: boolean) => {
    const query = isDesktop ? desktopSearchQuery : mobileSearchQuery
    if (query.length > 0) {
      if (isDesktop) setShowDesktopSuggestions(true)
      else setShowMobileSuggestions(true)
    }
  }

  const handleSearchBlur = (isDesktop: boolean) => {
    // Delay hiding suggestions to allow click events
    setTimeout(() => {
      if (isDesktop) setShowDesktopSuggestions(false)
      else setShowMobileSuggestions(false)
    }, 200)
  }

  return (
    <>
      {/* Top Navigation Bar */}
      <header className="top-bar">
        <div className="top-bar-container">
          <Link to="/" className="top-logo">
            <img src="/logo.jpg" alt="Unbiased Kratom" className="logo-image" />
            <span className="logo-text">Unbiased Kratom</span>
          </Link>
          
          <div className="top-search desktop-search">
            <form onSubmit={handleDesktopSearchSubmit} className="search-form">
              <div className="search-input-container">
                <input 
                  type="text" 
                  placeholder="Search 7OH, MIT, strains, effects..." 
                  className="search-bar"
                  value={desktopSearchQuery}
                  onChange={handleDesktopSearchChange}
                  onFocus={() => handleSearchFocus(true)}
                  onBlur={() => handleSearchBlur(true)}
                />
                <button type="submit" className="search-button">🔍</button>
                
                {showDesktopSuggestions && (
                  <div className="search-suggestions">
                    {getSearchSuggestions(desktopSearchQuery).map((suggestion, index) => (
                      <div
                        key={index}
                        className={`suggestion-item suggestion-${suggestion.type}`}
                        onMouseDown={() => handleSuggestionClick(suggestion, true)}
                      >
                        <span className="suggestion-icon">
                          {suggestion.type === 'vendor' ? '🏪' : 
                           suggestion.type === 'product' ? '📦' : '🔍'}
                        </span>
                        <span className="suggestion-text">{suggestion.text}</span>
                        {suggestion.type === 'vendor' && suggestion.data?.verified && (
                          <span className="verified-indicator">✓</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </form>
          </div>
          
          <div className="mobile-layout">
            <div className="mobile-signup-row">
              {!isLoggedIn && (
                <button className="action-btn mobile-signup" onClick={handleRegisterClick}>
                  Sign Up
                </button>
              )}
              {isLoggedIn && (
                <div className="user-menu mobile-user">
                  <NotificationSystem />
                  <Link to="/messages" className="action-btn" title="Messages">✉️</Link>
                  {user?.role === 'verified_vendor' && (
                    <Link to="/vendor-lounge" className="action-btn vendor-lounge-btn" title="Vendor Lounge">
                      🏪
                    </Link>
                  )}
                  <span className="user-greeting">
                    Hello, {user?.username}
                    {user?.isVerified && <span className="verified-badge">✓</span>}
                  </span>
                  <button className="logout-btn" onClick={handleLogout}>
                    Logout
                  </button>
                </div>
              )}
            </div>
            <div className="mobile-search-row">
              <button className="mobile-menu-btn" onClick={toggleMobileMenu}>
                ☰
              </button>
              <div className="top-search">
                <form onSubmit={handleMobileSearchSubmit} className="search-form">
                  <div className="search-input-container">
                    <input 
                      type="text" 
                      placeholder="Search 7OH, MIT, strains..." 
                      className="search-bar"
                      value={mobileSearchQuery}
                      onChange={handleMobileSearchChange}
                      onFocus={() => handleSearchFocus(false)}
                      onBlur={() => handleSearchBlur(false)}
                    />
                    <button type="submit" className="search-button">🔍</button>
                    
                    {showMobileSuggestions && (
                      <div className="search-suggestions">
                        {getSearchSuggestions(mobileSearchQuery).map((suggestion, index) => (
                          <div
                            key={index}
                            className={`suggestion-item suggestion-${suggestion.type}`}
                            onMouseDown={() => handleSuggestionClick(suggestion, false)}
                          >
                            <span className="suggestion-icon">
                              {suggestion.type === 'vendor' ? '🏪' : 
                               suggestion.type === 'product' ? '📦' : '🔍'}
                            </span>
                            <span className="suggestion-text">{suggestion.text}</span>
                            {suggestion.type === 'vendor' && suggestion.data?.verified && (
                              <span className="verified-indicator">✓</span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </form>
              </div>
              {!isLoggedIn && (
                <button className="login-btn mobile-login" onClick={handleLoginClick}>
                  Login
                </button>
              )}
            </div>
          </div>

          <div className="top-actions desktop-actions">
            {isLoggedIn ? (
              <>
                <NotificationSystem />
                <Link to="/messages" className="action-btn" title="Messages">✉️</Link>
                {user?.role === 'verified_vendor' && (
                  <Link to="/vendor-lounge" className="action-btn vendor-lounge-btn" title="Vendor Lounge">
                    🏪
                  </Link>
                )}
                <div className="user-menu">
                  <span className="user-greeting">
                    Hello, {user?.username}
                    {user?.isVerified && <span className="verified-badge">✓</span>}
                  </span>
                  <button className="logout-btn" onClick={handleLogout}>
                    Logout
                  </button>
                </div>
              </>
            ) : (
              <>
                <button className="action-btn" onClick={handleRegisterClick}>
                  Sign Up
                </button>
                <button className="login-btn" onClick={handleLoginClick}>
                  Login
                </button>
              </>
            )}
          </div>

        </div>
      </header>

      {/* Authentication Forms */}
      {showLoginForm && (
        <LoginForm onClose={closeAuthForms} onSwitchToRegister={switchToRegister} />
      )}
      {showRegisterForm && (
        <RegisterForm onClose={closeAuthForms} onSwitchToLogin={switchToLogin} />
      )}
      {showVendorApplication && (
        <VendorApplicationForm onClose={closeAuthForms} />
      )}

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="sidebar-overlay open" onClick={closeMobileMenu}></div>
      )}

      {/* Sidebar Navigation */}
      <nav className={`sidebar ${isMobileMenuOpen ? 'open' : ''}`}>
        <div className="sidebar-nav">
          <div className="nav-section">
            <h3 className="nav-section-title">Main</h3>
            <Link to="/" className="nav-item" onClick={closeMobileMenu}>
              <span className="nav-icon">🏠</span>
              Home
            </Link>
            <Link to="/search" className="nav-item featured-search" onClick={closeMobileMenu}>
              <span className="nav-icon">🔍</span>
              Advanced Search
            </Link>
            <Link to="/vendors" className="nav-item" onClick={closeMobileMenu}>
              <span className="nav-icon">🏪</span>
              Vendor Directory
            </Link>
            <Link to="/social" className="nav-item" onClick={closeMobileMenu}>
              <span className="nav-icon">🌐</span>
              Social Hub
            </Link>
            <Link to="/community" className="nav-item" onClick={closeMobileMenu}>
              <span className="nav-icon">💬</span>
              Community
            </Link>
            <Link to="/reviews" className="nav-item" onClick={closeMobileMenu}>
              <span className="nav-icon">⭐</span>
              Reviews
            </Link>
            {isLoggedIn && (
              <>
                <Link to="/messages" className="nav-item" onClick={closeMobileMenu}>
                  <span className="nav-icon">✉️</span>
                  Messages
                </Link>
                <Link to="/notifications" className="nav-item" onClick={closeMobileMenu}>
                  <span className="nav-icon">🔔</span>
                  Notifications
                </Link>
              </>
            )}
          </div>

          <div className="nav-section">
            <h3 className="nav-section-title">Categories</h3>
            <Link to="/vendors?category=traditional" className="nav-item">
              <span className="nav-icon">🌿</span>
              Traditional Kratom
            </Link>
            <Link to="/vendors?category=7oh-tablets" className="nav-item">
              <span className="nav-icon">⊕</span>
              7-OH Tablets
            </Link>
            <Link to="/vendors?category=7oh-powder" className="nav-item">
              <span className="nav-icon">⚗️</span>
              7-OH Powder
            </Link>
            <Link to="/vendors?category=extracts" className="nav-item">
              <span className="nav-icon">💧</span>
              Other Extracts
            </Link>
            <Link to="/vendors?category=capsules" className="nav-item">
              <span className="nav-icon">💊</span>
              Capsules
            </Link>
            <Link to="/vendors?category=powder" className="nav-item">
              <span className="nav-icon">🥄</span>
              Powder
            </Link>
          </div>

          <div className="nav-section">
            <h3 className="nav-section-title">Rewards</h3>
            <Link to="/rewards" className="nav-item" onClick={closeMobileMenu}>
              <span className="nav-icon">🎁</span>
              Rewards Program
            </Link>
            <Link to="/referrals" className="nav-item" onClick={closeMobileMenu}>
              <span className="nav-icon">💸</span>
              My Referrals
            </Link>
          </div>

          <div className="nav-section">
            <h3 className="nav-section-title">Educational</h3>
            <Link to="/lab-verification" className="nav-item featured-search" onClick={closeMobileMenu}>
              <span className="nav-icon">🔬</span>
              Lab Verification
            </Link>
            <Link to="/taper-tracker" className="nav-item" onClick={closeMobileMenu}>
              <span className="nav-icon">📊</span>
              Taper Tracker Pro
            </Link>
            <Link to="/education/dosing" className="nav-item" onClick={closeMobileMenu}>
              <span className="nav-icon">⚗️</span>
              Dosing
            </Link>
            <Link to="/education/potentiators" className="nav-item" onClick={closeMobileMenu}>
              <span className="nav-icon">🧪</span>
              Potentiators
            </Link>
            <Link to="/education/tapering" className="nav-item" onClick={closeMobileMenu}>
              <span className="nav-icon">📉</span>
              Tapering
            </Link>
            <Link to="/education/laws" className="nav-item" onClick={closeMobileMenu}>
              <span className="nav-icon">⚖️</span>
              Laws
            </Link>
          </div>

          <div className="nav-section">
            <h3 className="nav-section-title">About</h3>
            <Link to="/mission" className="nav-item" onClick={closeMobileMenu}>
              <span className="nav-icon">🎯</span>
              Our Mission
            </Link>
            <Link to="/compare" className="nav-item" onClick={closeMobileMenu}>
              <span className="nav-icon">📊</span>
              Compare Vendors
            </Link>
            <Link to="/deals" className="nav-item" onClick={closeMobileMenu}>
              <span className="nav-icon">💰</span>
              Deals & Sales
            </Link>
            <Link to="/blog" className="nav-item" onClick={closeMobileMenu}>
              <span className="nav-icon">📰</span>
              Blog
            </Link>
          </div>

          <div className="nav-section">
            <h3 className="nav-section-title">Vendors</h3>
            <Link to="/verification" className="nav-item" onClick={closeMobileMenu}>
              <span className="nav-icon">✅</span>
              Get Verified
            </Link>
            <button onClick={handleVendorApplicationClick} className="nav-item nav-button">
              <span className="nav-icon">🏪</span>
              Apply for Verification
            </button>
            <Link to="/vendor-dashboard" className="nav-item">
              <span className="nav-icon">📊</span>
              Vendor Dashboard
            </Link>
          </div>

          {user?.role === 'admin' && (
            <div className="nav-section">
              <h3 className="nav-section-title">Admin</h3>
              <Link to="/staff-dashboard" className="nav-item" onClick={closeMobileMenu}>
                <span className="nav-icon">🛠️</span>
                Staff Dashboard
              </Link>
              <Link to="/admin-dashboard" className="nav-item" onClick={closeMobileMenu}>
                <span className="nav-icon">⚙️</span>
                Admin Dashboard
              </Link>
              <Link to="/analytics" className="nav-item" onClick={closeMobileMenu}>
                <span className="nav-icon">📈</span>
                Analytics Dashboard
              </Link>
              <Link to="/admin-tools" className="nav-item" onClick={closeMobileMenu}>
                <span className="nav-icon">🛠️</span>
                Admin Tools Overview
              </Link>
            </div>
          )}

          {(user?.role === 'admin' || user?.role === 'staff') && (
            <div className="nav-section">
              <h3 className="nav-section-title">Platform Tools</h3>
              <Link to="/trusted-tester-application" className="nav-item" onClick={closeMobileMenu}>
                <span className="nav-icon">⭐</span>
                Trusted Tester Program
              </Link>
              <Link to="/vendor-accountability" className="nav-item" onClick={closeMobileMenu}>
                <span className="nav-icon">🎯</span>
                Vendor Accountability
              </Link>
              <Link to="/quality-compliance" className="nav-item" onClick={closeMobileMenu}>
                <span className="nav-icon">✅</span>
                Quality Compliance
              </Link>
              <Link to="/review-integrity" className="nav-item" onClick={closeMobileMenu}>
                <span className="nav-icon">🛡️</span>
                Review Integrity
              </Link>
            </div>
          )}
        </div>
      </nav>
    </>
  )
}